import React from "react"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import { MultiStepFlowContainer } from "./MultiStepFlow"

/**
 * SetupFlowLayout
 *
 * Wrapper around MultiStepFlowContainer specifically for setup checklist subflows.
 * Provides custom back button behavior where the first step navigates back to /setup
 * instead of being disabled.
 *
 * @param {Object} props
 * @param {Array} props.steps - Array of step configuration objects
 * @param {Object} props.initialValues - Initial form values
 * @param {Function} props.onComplete - Callback when flow is completed (receives values and stepId)
 * @param {string} props.stepId - The ID of the current setup step (for marking complete)
 * @param {string} props.completeButtonText - Text for the complete button
 * @param {string} props.nextButtonText - Text for the next button
 * @param {React.ReactNode} props.customContentAfterButton - Custom content to render after the next/complete button
 * @param {Object} props.buttonProps - Props to apply to both next and complete buttons (width, bgColor, rounded, padding, etc.)
 * @param {Object} props.nextButtonProps - Props to apply specifically to the next button (overrides buttonProps)
 * @param {Object} props.completeButtonProps - Props to apply specifically to the complete button (overrides buttonProps)
 */
const SetupFlowLayout = ({
  steps,
  initialValues,
  onComplete,
  stepId,
  completeButtonText,
  nextButtonText,
  buttonProps,
  nextButtonProps,
  completeButtonProps,
  ...otherProps
}) => {
  const navigate = useNavigate()

  // Custom back handler for the first step
  const handleCustomBack = (currentStepIndex) => {
    if (currentStepIndex === 0) {
      // Navigate back to setup checklist
      navigate("..")
      return true // Signal that we handled the back navigation
    }
    return false // Let MultiStepFlowContainer handle it
  }

  // Wrap the onComplete callback to navigate back to setup checklist
  const handleComplete = async (values) => {
    // Call the provided onComplete with values and stepId
    if (onComplete) {
      await onComplete(values, stepId)
    } else {
      navigate("/")
    }
  }

  return (
    <MultiStepFlowContainer
      steps={steps}
      initialValues={initialValues}
      onComplete={handleComplete}
      onBack={handleCustomBack}
      alwaysShowBackButton={true}
      showBackButton={true}
      showProgress={true}
      completeButtonText={completeButtonText || "Complete"}
      nextButtonText={nextButtonText || "Next"}
      buttonProps={buttonProps}
      nextButtonProps={nextButtonProps}
      completeButtonProps={completeButtonProps}
      {...otherProps}
    />
  )
}

SetupFlowLayout.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.object).isRequired,
  initialValues: PropTypes.object,
  onComplete: PropTypes.func,
  stepId: PropTypes.string.isRequired,
  completeButtonText: PropTypes.string,
  nextButtonText: PropTypes.string,
  customContentAfterButton: PropTypes.node,
  buttonProps: PropTypes.object,
  nextButtonProps: PropTypes.object,
  completeButtonProps: PropTypes.object,
}

export default SetupFlowLayout